import React, { ComponentType, FC, memo, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Tabs, TabsModifiers } from '@om-fe/widgets/dist/components/tabs/tabs';
import { TabItemModel } from '@om-fe/widgets/dist/components/tabs/tabs.types';
import { PlatformTypes } from '@om-fe/utils';
import { bemSlider } from '@om-fe/widgets/dist/components/slider/slider.constants';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { AppState } from '@om-fe/core/core/types';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import MatchOfTheDay from './match-of-the-day';

interface MatchOfTheDayContentTabsStoreProps {
    readonly deviceType?: PlatformTypes;
}

export enum MatchOfTheDayContentTabsIds {
    Today = 'Today',
    Tomorrow = 'Tomorrow',
}

export type MatchOfTheDayContentTabsProps = MatchOfTheDayContentTabsStoreProps
    & I18nContextInterface;

interface TabHeaderItemProps {
    readonly name: string;
    readonly numberOfEvents?: number
}

const modifiers: TabsModifiers = {
    tabsWrapperModifiers: {
        LiveUpcoming: true,
        MatchOfTheDay: true
    },
    headerModifiers: {
        ItemFullEqualWidth: true,
        Small10: true,
        Tertiary: true,
    },
};

const TabHeaderItem: FC<TabHeaderItemProps> = memo(function TabHeaderItemComponent({ name }: TabHeaderItemProps
) {
    return (
        <span className={bemSlider('ItemText')}>{name}</span>
    );
})

function MatchOfTheDayContentTabsComponent(
    {
        deviceType,
    }: MatchOfTheDayContentTabsProps
) {
    const { gettext } = useI18NContext();
    const [selectedTab, setSelectedTab] = useState<string>(MatchOfTheDayContentTabsIds.Today);
    const [disciplineId, setDisciplineId] = useState<string | undefined>(undefined);

    const onTabSelect = useCallback((selectedTabIdNewValue: string): void => {
        if (selectedTab === selectedTabIdNewValue) {
            return;
        }

        setSelectedTab(selectedTabIdNewValue);
        setDisciplineId(disciplineId);
    }, [selectedTab, disciplineId]);

    const onSelectDiscipline = useCallback((selectedDisciplineId: string): void => {
        if (disciplineId === selectedDisciplineId || typeof selectedDisciplineId === 'undefined') {
            return;
        }

        setDisciplineId(selectedDisciplineId);
    }, [disciplineId]);

    const tabsList: TabItemModel[] = useMemo((): TabItemModel[] => {
        const tabItem: TabItemModel[] = [
            {
                tabHeader: (<TabHeaderItem name={gettext("Today's Match")} />),
                tabContent: (
                    <MatchOfTheDay
                        activeTab={MatchOfTheDayContentTabsIds.Today}
                        onSelectDiscipline={onSelectDiscipline}
                        selectDisciplineId={disciplineId}
                    />
                ),
                tabId: MatchOfTheDayContentTabsIds.Today
            },{
                tabHeader: (<TabHeaderItem name={gettext("Tomorrow's Match")} />),
                tabContent: (
                    <MatchOfTheDay
                        activeTab={MatchOfTheDayContentTabsIds.Tomorrow}
                        onSelectDiscipline={onSelectDiscipline}
                        selectDisciplineId={disciplineId}
                    />
                ),
                tabId: MatchOfTheDayContentTabsIds.Tomorrow
            }
        ];

        return tabItem;
    }, [gettext, disciplineId]);

    return (
            <Tabs
                hasArrows={false}
                modifiers={modifiers}
                tabsList={tabsList}
                activeTabId={selectedTab}
                onTabSelect={onTabSelect}
            />
    );
}

const MatchOfTheDayContentTabs: ComponentType<MatchOfTheDayContentTabsStoreProps> = compose<ComponentType<MatchOfTheDayContentTabsStoreProps>>(
    withTranslation<MatchOfTheDayContentTabsProps>(),
    connect<MatchOfTheDayContentTabsStoreProps>(
        (state: AppState): MatchOfTheDayContentTabsStoreProps => {
            const { deviceInfo } = state;

            return {
                deviceType: deviceInfo?.platform.type,
            };
        },
    ),
)(MatchOfTheDayContentTabsComponent);
export default MatchOfTheDayContentTabs;
